import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  collection, 
  addDoc, 
  doc, 
  getDoc, 
  updateDoc,
  serverTimestamp,
  query,
  where,
  getDocs 
} from 'firebase/firestore';
import { db, auth } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { 
  FaCalendarAlt, 
  FaClock, 
  FaBook, 
  FaUserGraduate, 
  FaMoneyBill, 
  FaCheckCircle,
  FaVideo,
  FaUserFriends,
  FaMapMarkerAlt,
  FaExclamationCircle,
  FaSpinner,
  FaArrowLeft,
  FaBell,
  FaStar,
  FaInfoCircle,
  FaLock,
  FaCreditCard,
  FaShieldAlt
} from 'react-icons/fa';
import Navbar from '../layout/Navbar';

const BookingForm = () => {
  const { tutorId } = useParams();
  const { user, userData } = useAuth();
  const navigate = useNavigate();
  
  const [tutor, setTutor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [existingBooking, setExistingBooking] = useState(false);
  
  const [formData, setFormData] = useState({
    date: new Date(new Date().setHours(new Date().getHours() + 2, 0, 0, 0)), // Default: 2 hours from now
    duration: '1',
    subject: '',
    level: '',
    notes: '',
    meetingType: 'online',
    location: '',
    paymentMethod: 'card'
  });

  const subjects = [
    'Mathematics', 'Physics', 'Chemistry', 'Biology',
    'English', 'French', 'Computer Science', 'Economics',
    'Accounting', 'History', 'Geography', 'Literature',
    'Business Studies', 'Further Mathematics', 'Art', 'Music'
  ];

  const levels = [
    { value: 'primary', label: 'Primary School', icon: '🏫' },
    { value: 'secondary-junior', label: 'Secondary (Form 1-2)', icon: '📚' },
    { value: 'secondary-senior', label: 'Secondary (Form 3-5)', icon: '🎓' },
    { value: 'sixth-lower', label: 'Sixth Form (Lower)', icon: '📖' },
    { value: 'sixth-upper', label: 'Sixth Form (Upper)', icon: '🎯' },
    { value: 'university', label: 'University', icon: '🎓' },
    { value: 'professional', label: 'Professional', icon: '💼' }
  ];

  const durations = [
    { value: '1', label: '1 hour', priceMultiplier: 1 },
    { value: '1.5', label: '1.5 hours', priceMultiplier: 1.5 },
    { value: '2', label: '2 hours', priceMultiplier: 2 },
    { value: '2.5', label: '2.5 hours', priceMultiplier: 2.5 },
    { value: '3', label: '3 hours', priceMultiplier: 3 }
  ];

  const meetingTypes = [
    { 
      value: 'online', 
      label: 'Online Session', 
      icon: <FaVideo />, 
      desc: 'Video call via Zoom/Google Meet',
      color: '#667eea'
    },
    { 
      value: 'in-person', 
      label: 'In Person', 
      icon: <FaUserFriends />, 
      desc: 'Face-to-face meeting',
      color: '#43e97b'
    },
    { 
      value: 'both', 
      label: 'Either is fine', 
      icon: <FaCheckCircle />, 
      desc: 'Open to both options',
      color: '#ff9800'
    }
  ];

  const paymentMethods = [
    { value: 'card', label: 'Credit/Debit Card', icon: <FaCreditCard /> },
    { value: 'mobile', label: 'Mobile Money', icon: '📱' },
    { value: 'bank', label: 'Bank Transfer', icon: '🏦' }
  ];

  useEffect(() => {
    fetchTutorData();
    checkExistingBookings();
  }, [tutorId]);

  const fetchTutorData = async () => {
    try {
      const [tutorDoc, userDoc] = await Promise.all([
        getDoc(doc(db, 'tutors', tutorId)),
        getDoc(doc(db, 'users', tutorId))
      ]);
      
      if (tutorDoc.exists() && userDoc.exists()) {
        setTutor({ ...tutorDoc.data(), ...userDoc.data() });
        
        // Set first subject as default if available
        if (tutorDoc.data()?.subjects?.[0]) {
          setFormData(prev => ({
            ...prev,
            subject: tutorDoc.data().subjects[0]
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching tutor data:', error);
      setError('Failed to load tutor information');
    } finally {
      setLoading(false);
    }
  };

  const checkExistingBookings = async () => {
    if (!user) return;
    
    try {
      const bookingsQuery = query(
        collection(db, 'bookings'),
        where('studentId', '==', user.uid),
        where('tutorId', '==', tutorId),
        where('status', 'in', ['pending', 'confirmed'])
      );
      
      const snapshot = await getDocs(bookingsQuery);
      if (!snapshot.empty) {
        setExistingBooking(true);
        setError('You already have a pending or confirmed booking with this tutor');
      }
    } catch (error) {
      console.error('Error checking existing bookings:', error);
    }
  };

  const calculateTotal = () => {
    const rate = tutor?.hourlyRate || 0;
    const duration = parseFloat(formData.duration);
    const selectedDuration = durations.find(d => d.value === formData.duration);
    const multiplier = selectedDuration?.priceMultiplier || 1;
    return (rate * multiplier).toFixed(2);
  };

  const calculateSavings = () => {
    if (parseFloat(formData.duration) >= 2) {
      const total = parseFloat(calculateTotal());
      const savings = total * 0.1; // 10% savings for 2+ hours
      return savings.toFixed(2);
    }
    return 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      navigate('/login', { state: { from: `/book/${tutorId}` } });
      return;
    }

    if (existingBooking) {
      setError('You already have an active booking with this tutor');
      return;
    }

    // Validation
    const validationErrors = [];
    if (!formData.subject) validationErrors.push('Please select a subject');
    if (!formData.level) validationErrors.push('Please select academic level');
    if (formData.date < new Date()) validationErrors.push('Please select a future date and time');
    if (formData.meetingType === 'in-person' && !formData.location.trim()) {
      validationErrors.push('Please specify meeting location for in-person sessions');
    }

    if (validationErrors.length > 0) {
      setError(validationErrors[0]);
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const bookingData = {
        studentId: user.uid,
        tutorId: tutorId,
        studentName: userData?.fullName || user.email,
        tutorName: tutor?.fullName,
        date: formData.date,
        duration: parseFloat(formData.duration),
        subject: formData.subject,
        level: formData.level,
        notes: formData.notes,
        meetingType: formData.meetingType,
        location: formData.location,
        totalAmount: calculateTotal(),
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        paymentStatus: 'pending',
        paymentMethod: formData.paymentMethod,
        meetingLink: '',
        studentEmail: user.email,
        tutorEmail: tutor?.email,
        tutorProfileImage: tutor?.profileImage,
        savings: calculateSavings()
      };

      const bookingRef = await addDoc(collection(db, 'bookings'), bookingData);

      // Update tutor's total sessions count
      await updateDoc(doc(db, 'tutors', tutorId), {
        totalSessions: (tutor?.totalSessions || 0) + 1
      });

      // Create notification for tutor
      const notificationData = {
        userId: tutorId,
        type: 'new_booking',
        title: 'New Booking Request 🎯',
        message: `${userData?.fullName || 'A student'} requested a ${formData.duration}-hour ${formData.subject} session`,
        bookingId: bookingRef.id,
        read: false,
        createdAt: serverTimestamp(),
        actionUrl: `/dashboard/bookings/${bookingRef.id}`
      };

      await addDoc(collection(db, 'notifications'), notificationData);

      // Also notify student
      const studentNotification = {
        userId: user.uid,
        type: 'booking_confirmation',
        title: 'Booking Request Sent ✅',
        message: `Your session request with ${tutor?.fullName} has been sent`,
        bookingId: bookingRef.id,
        read: false,
        createdAt: serverTimestamp(),
        actionUrl: `/dashboard/bookings/${bookingRef.id}`
      };

      await addDoc(collection(db, 'notifications'), studentNotification);

      setSuccess('Booking request sent successfully! Redirecting to dashboard...');
      
      setTimeout(() => {
        navigate('/dashboard/bookings');
      }, 2000);
    } catch (error) {
      console.error('Booking error:', error);
      setError(error.code === 'unavailable' 
        ? 'Network error. Please check your connection.' 
        : 'Failed to create booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="booking-form-page">
          <div className="booking-form-container">
            <div className="loading-state">
              <div className="loading-spinner-large animate-spin"></div>
              <h3>Loading Booking Information</h3>
              <p>Preparing your session with the tutor...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!tutor) {
    return (
      <>
        <Navbar />
        <div className="booking-form-page">
          <div className="booking-form-container">
            <div className="error-state">
              <FaExclamationCircle className="error-icon" />
              <h2>Tutor Not Available</h2>
              <p>The tutor profile you're looking for is no longer available or has been deactivated.</p>
              <button 
                onClick={() => navigate('/search')} 
                className="btn btn-primary mt-4"
              >
                Browse Other Tutors
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="booking-form-page">
        <div className="booking-form-container">
          {/* Header */}
          <div className="booking-form-header animate-fade-in">
            <div className="header-content">
              <button 
                className="btn-back"
                onClick={() => navigate(`/tutor/${tutorId}`)}
              >
                <FaArrowLeft /> Back to Profile
              </button>
              <h1>
                <FaCalendarAlt /> Book a Session
              </h1>
              <p className="header-subtitle">
                Schedule your personalized learning session with{' '}
                <span className="tutor-highlight">{tutor.fullName}</span>
              </p>
            </div>
          </div>

          <div className="booking-form-layout">
            {/* Main Form */}
            <div className="form-card animate-fade-in-up">
              {error && (
                <div className="form-message error animate-fade-in">
                  <FaExclamationCircle className="icon" />
                  <div className="message-content">
                    <strong>Oops!</strong>
                    <span>{error}</span>
                  </div>
                </div>
              )}

              {success && (
                <div className="form-message success animate-fade-in">
                  <FaCheckCircle className="icon" />
                  <div className="message-content">
                    <strong>Success!</strong>
                    <span>{success}</span>
                  </div>
                </div>
              )}

              <div className="form-header">
                <h2>
                  <FaUserGraduate /> Session Details
                </h2>
                <p className="form-subtitle">Fill in your learning requirements</p>
              </div>

              <form onSubmit={handleSubmit} className="booking-form">
                {/* Date & Time Section */}
                <div className="form-section animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                  <div className="section-header">
                    <div className="section-icon">
                      <FaCalendarAlt />
                    </div>
                    <div>
                      <h3 className="section-title">Select Date & Time</h3>
                      <p className="section-description">Choose when you'd like your session</p>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label required">
                      <span>Session Date & Time</span>
                      <span className="label-note">Select a future date and time</span>
                    </label>
                    <div className="date-picker-container">
                      <DatePicker
                        selected={formData.date}
                        onChange={(date) => setFormData({...formData, date})}
                        showTimeSelect
                        minDate={new Date()}
                        minTime={new Date().setHours(8, 0)}
                        maxTime={new Date().setHours(22, 0)}
                        dateFormat="MMMM d, yyyy h:mm aa"
                        placeholderText="Choose date and time"
                        className="date-picker-input"
                        popperClassName="date-picker-popper"
                        calendarClassName="date-picker-calendar"
                      />
                      <div className="date-picker-info">
                        <FaInfoCircle />
                        <span>Sessions available from 8 AM to 10 PM</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Duration Section */}
                <div className="form-section animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                  <div className="section-header">
                    <div className="section-icon">
                      <FaClock />
                    </div>
                    <div>
                      <h3 className="section-title">Session Duration</h3>
                      <p className="section-description">How long would you like your session?</p>
                    </div>
                  </div>
                  <div className="duration-selector">
                    {durations.map(duration => (
                      <div key={duration.value} className="duration-option">
                        <input
                          type="radio"
                          id={`duration-${duration.value}`}
                          name="duration"
                          value={duration.value}
                          checked={formData.duration === duration.value}
                          onChange={(e) => setFormData({...formData, duration: e.target.value})}
                          className="duration-input"
                        />
                        <label htmlFor={`duration-${duration.value}`} className="duration-label">
                          <span className="duration-time">{duration.label}</span>
                          <span className="duration-price">
                            ${(tutor.hourlyRate * duration.priceMultiplier).toFixed(2)}
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Subject & Level Section */}
                <div className="form-section animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                  <div className="section-header">
                    <div className="section-icon">
                      <FaBook />
                    </div>
                    <div>
                      <h3 className="section-title">Learning Details</h3>
                      <p className="section-description">What and at what level would you like to learn?</p>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label required">
                        Subject to Learn
                        <span className="label-note">Select from tutor's specialties</span>
                      </label>
                      <div className="subject-selector">
                        {tutor.subjects?.slice(0, 8).map(subject => (
                          <button
                            key={subject}
                            type="button"
                            className={`subject-btn ${formData.subject === subject ? 'active' : ''}`}
                            onClick={() => setFormData({...formData, subject})}
                          >
                            {subject}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label required">
                        Academic Level
                        <span className="label-note">Select your current level</span>
                      </label>
                      <div className="level-selector">
                        {levels.map(level => (
                          <div key={level.value} className="level-option">
                            <input
                              type="radio"
                              id={`level-${level.value}`}
                              name="level"
                              value={level.value}
                              checked={formData.level === level.value}
                              onChange={(e) => setFormData({...formData, level: e.target.value})}
                              className="level-input"
                            />
                            <label htmlFor={`level-${level.value}`} className="level-label">
                              <span className="level-icon">{level.icon}</span>
                              <span className="level-name">{level.label}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Meeting Type Section */}
                <div className="form-section animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                  <div className="section-header">
                    <div className="section-icon">
                      <FaVideo />
                    </div>
                    <div>
                      <h3 className="section-title">Meeting Type</h3>
                      <p className="section-description">How would you like to meet?</p>
                    </div>
                  </div>
                  <div className="meeting-type-selector">
                    {meetingTypes.map(type => (
                      <div key={type.value} className="meeting-type-option">
                        <input
                          type="radio"
                          id={`type-${type.value}`}
                          name="meetingType"
                          value={type.value}
                          checked={formData.meetingType === type.value}
                          onChange={(e) => setFormData({...formData, meetingType: e.target.value})}
                          className="meeting-type-input"
                        />
                        <label 
                          htmlFor={`type-${type.value}`} 
                          className="meeting-type-label"
                          style={{ '--type-color': type.color }}
                        >
                          <div className="meeting-type-icon" style={{ color: type.color }}>
                            {type.icon}
                          </div>
                          <div className="meeting-type-content">
                            <span className="meeting-type-text">{type.label}</span>
                            <small className="meeting-type-desc">{type.desc}</small>
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>

                  {formData.meetingType === 'in-person' && (
                    <div className="location-input-group animate-fade-in">
                      <label className="form-label required">
                        <FaMapMarkerAlt /> Meeting Location
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.location}
                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                        placeholder="Enter meeting address or preferred location"
                        required
                      />
                      <div className="input-info">
                        <FaInfoCircle /> Please provide a safe and public location
                      </div>
                    </div>
                  )}
                </div>

                {/* Additional Notes */}
                <div className="form-section animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
                  <div className="section-header">
                    <div className="section-icon">
                      <FaUserGraduate />
                    </div>
                    <div>
                      <h3 className="section-title">Additional Information</h3>
                      <p className="section-description">Help the tutor prepare for your session</p>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      Special Requests or Topics
                      <span className="label-note">Optional but helpful</span>
                    </label>
                    <textarea
                      className="form-textarea"
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="Any specific topics, materials, or learning goals you'd like to focus on during the session..."
                      rows="4"
                    />
                  </div>
                </div>

                {/* Payment Method (Optional) */}
                <div className="form-section animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
                  <div className="section-header">
                    <div className="section-icon">
                      <FaCreditCard />
                    </div>
                    <div>
                      <h3 className="section-title">Payment Method</h3>
                      <p className="section-description">How would you like to pay?</p>
                    </div>
                  </div>
                  <div className="payment-selector">
                    {paymentMethods.map(method => (
                      <div key={method.value} className="payment-option">
                        <input
                          type="radio"
                          id={`payment-${method.value}`}
                          name="paymentMethod"
                          value={method.value}
                          checked={formData.paymentMethod === method.value}
                          onChange={(e) => setFormData({...formData, paymentMethod: e.target.value})}
                          className="payment-input"
                        />
                        <label htmlFor={`payment-${method.value}`} className="payment-label">
                          <span className="payment-icon">{method.icon}</span>
                          <span className="payment-text">{method.label}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Form Actions */}
                <div className="form-actions animate-fade-in-up" style={{ animationDelay: '0.7s' }}>
                  <div className="actions-content">
                    <div className="security-notice">
                      <FaLock className="lock-icon" />
                      <span>Your booking is secured with end-to-end encryption</span>
                    </div>
                    <div className="action-buttons">
                      <button
                        type="button"
                        className="btn btn-outline btn-lg"
                        onClick={() => navigate(`/tutor/${tutorId}`)}
                        disabled={submitting}
                      >
                        <FaArrowLeft /> Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary btn-lg"
                        disabled={submitting || existingBooking}
                      >
                        {submitting ? (
                          <>
                            <FaSpinner className="spinner-icon animate-spin" /> Processing...
                          </>
                        ) : (
                          <>
                            <FaBell /> Request Booking
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            {/* Summary Sidebar */}
            <div className="summary-sidebar animate-fade-in-up" style={{ animationDelay: '0.8s' }}>
              {/* Tutor Summary */}
              <div className="tutor-summary-card">
                <div className="tutor-header">
                  <div className="tutor-avatar">
                    <img 
                      src={tutor.profileImage || 'https://via.placeholder.com/150'} 
                      alt={tutor.fullName}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/150';
                      }}
                    />
                  </div>
                  <div className="tutor-info">
                    <h3 className="tutor-name">{tutor.fullName}</h3>
                    <div className="tutor-rating">
                      <FaStar className="star-icon filled" />
                      <span className="rating-value">{tutor.rating?.toFixed(1) || 'New'}</span>
                      <span className="rating-count">({tutor.totalReviews || 0} reviews)</span>
                    </div>
                  </div>
                </div>
                <div className="tutor-specialties">
                  <h4>Specialties</h4>
                  <div className="specialties-tags">
                    {tutor.subjects?.slice(0, 4).map((subject, index) => (
                      <span key={index} className="specialty-tag">
                        {subject}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Price Summary */}
              <div className="price-summary-card">
                <h3>
                  <FaMoneyBill /> Price Summary
                </h3>
                <div className="price-breakdown">
                  <div className="price-item">
                    <span className="price-label">Hourly Rate</span>
                    <span className="price-value">${tutor.hourlyRate || 0}/hour</span>
                  </div>
                  <div className="price-item">
                    <span className="price-label">Duration</span>
                    <span className="price-value">{formData.duration} hour(s)</span>
                  </div>
                  {parseFloat(formData.duration) >= 2 && (
                    <div className="price-item discount">
                      <span className="price-label">
                        <FaCheckCircle /> 10% Savings
                      </span>
                      <span className="price-value">-${calculateSavings()}</span>
                    </div>
                  )}
                  <div className="price-total">
                    <span className="total-label">Total Amount</span>
                    <span className="total-value">${calculateTotal()}</span>
                  </div>
                </div>
                
                {/* Booking Info */}
                <div className="booking-info">
                  <h4>
                    <FaInfoCircle /> Booking Information
                  </h4>
                  <ul>
                    <li>
                      <FaCheckCircle /> Payment only after tutor confirms
                    </li>
                    <li>
                      <FaShieldAlt /> 24-hour cancellation policy
                    </li>
                    <li>
                      <FaVideo /> Meeting link provided for online
                    </li>
                    <li>
                      <FaUserFriends /> Free reschedule up to 6 hours before
                    </li>
                  </ul>
                </div>

                {/* Payment Security */}
                <div className="payment-security">
                  <FaLock className="security-icon" />
                  <div className="security-info">
                    <strong>Secure Payment</strong>
                    <span>Your payment details are encrypted and secure</span>
                  </div>
                </div>
              </div>

              {/* Session Preview */}
              <div className="session-preview">
                <h4>Your Session Preview</h4>
                <div className="preview-item">
                  <span className="preview-label">Date & Time</span>
                  <span className="preview-value">
                    {formData.date.toLocaleDateString('en-US', { 
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <div className="preview-item">
                  <span className="preview-label">Subject</span>
                  <span className="preview-value">{formData.subject || 'Not selected'}</span>
                </div>
                <div className="preview-item">
                  <span className="preview-label">Meeting Type</span>
                  <span className="preview-value">
                    {formData.meetingType === 'online' ? 'Online' : 
                     formData.meetingType === 'in-person' ? 'In Person' : 'Either'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookingForm;